import { useEffect, useRef, useState } from "react";
import { useDesign } from "./state/designStore";
import { TemplateGrid, PremiumBlockedModal } from "./components/TemplateGrid";
import { FolderGrid } from "./components/FolderGrid";
import { ControlsPanel } from "./components/ControlsPanel";
import { PackPicker } from "./components/PackPicker";
import { BulkCreateScreen } from "./components/BulkCreateScreen";
import { PacksBrowser } from "./components/PacksBrowser";
import { CreateMenu } from "./components/CreateMenu";
import { AccountScreen } from "./components/AccountScreen";
import { AppHeader } from "./components/AppHeader";
import { BottomNav, MainTab } from "./components/BottomNav";
import { PlansScreen } from "./components/PlansScreen";
import { GuideScreen } from "./components/GuideScreen";
import { ScreenHeader } from "./components/ScreenHeader";
import { UndoRedoBar } from "./components/UndoRedoBar";
import { RecentProjectsScreen, VipFeatureModal } from "./components/RecentProjectsScreen";
import { RenderingToast } from "./components/RenderingToast";
import { api, ApiError } from "./lib/api";
import { AccountOut, FolderOut, RenderFinalizeResponse, TemplateOut } from "./types";
import { hideBackButton, showBackButton } from "./lib/telegram";
const BLANK_TEMPLATE_ID = "_blank_transparent";
const EMOJI_ONLY_PATTERN = /^(?:[\u{1F1E6}-\u{1F1FF}]{2}|[0-9#*]\u{FE0F}?\u{20E3}|(?:[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}])(?:\u{FE0F})?(?:\u{200D}(?:[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}])(?:\u{FE0F})?)*)$/u;
function detectEmojiFromText(text: string | undefined | null): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > 8) return null;
  return EMOJI_ONLY_PATTERN.test(trimmed) ? trimmed : null;
}

type Screen = "account" | "createMenu" | "packs" | "plans" | "guide" | "folders" | "templates" | "favorites" | "recent" | "editor" | "finalize";
type EditorOrigin = "templates" | "textOnly" | "recent";

const ROOT_SCREENS: Screen[] = ["account", "createMenu", "packs", "plans", "guide"];

function screenToTab(screen: Screen): MainTab {
  if (screen === "account") return "home";
  if (screen === "packs") return "packs";
  if (screen === "plans") return "plans";
  if (screen === "guide") return "guide";
  return "create"; // createMenu, folders, templates, editor, finalize are all part of the creation flow
}

function EditorScreen({ onBack, onFinalized }: { onBack: () => void; onFinalized: (stickerId: string) => void }) {
  const { design, activeText, template } = useDesign();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tier, setTier] = useState<AccountOut["membership"]["tier"] | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Bulk creation is templates-only - the text-only/transparent-background
  // flow isn't eligible, regardless of tier.
  const isTemplateSticker = template?.id !== BLANK_TEMPLATE_ID;
  const canBulkCreate = (tier === "pro" || tier === "vip") && isTemplateSticker;

  useEffect(() => {
    api
      .get<AccountOut>("/api/account")
      .then((res) => setTier(res.membership.tier))
      .catch(() => undefined);
  }, []);

  const handleFinalize = async () => {
    if (!design) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.post<RenderFinalizeResponse>("/api/render/finalize", { design });
      onFinalized(res.sticker_id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "فشل الحفظ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <ScreenHeader title="تصميم الملصق" onBack={onBack} right={<UndoRedoBar />} />
      <ControlsPanel />
      <div className="sticky bottom-0 bg-tg-bg p-4 border-t border-white/10 flex flex-col gap-2">
        {error && <p className="text-red-400 text-sm">{error}</p>}
        <button
          type="button"
          disabled={
            saving ||
            !(
              design?.texts.some((t) => t.text.trim()) ||
              (design?.effects.length ?? 0) > 0 ||
              (design?.drawings.length ?? 0) > 0
            )
          }
          onClick={handleFinalize}
          className="w-full py-3 rounded-xl bg-tg-button text-tg-buttonText font-semibold disabled:opacity-40"
        >
          {saving ? "جاري الحفظ..." : "متابعة"}
        </button>
        {canBulkCreate && activeText && activeText.text.trim() && (
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            className="w-full py-2.5 rounded-xl glass-pill text-sm font-medium"
          >
            ✨ إنشاء عدة أسماء دفعة وحدة
          </button>
        )}
      </div>

      {bulkOpen && design && activeText && (
        <BulkCreateScreen design={design} targetTextId={activeText.id} onClose={() => setBulkOpen(false)} />
      )}
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState<Screen>("account");
  const [editorOrigin, setEditorOrigin] = useState<EditorOrigin>("templates");
  const [selectedFolder, setSelectedFolder] = useState<FolderOut | null>(null);
  const [stickerId, setStickerId] = useState<string | null>(null);
  const [resolvingDeepLink, setResolvingDeepLink] = useState(true);
  const [tier, setTier] = useState<AccountOut["membership"]["tier"] | null>(null);
  const [blockedTemplate, setBlockedTemplate] = useState<TemplateOut | null>(null);
  const [vipModal, setVipModal] = useState(false);
  const { design, startDesign, overlayClose } = useDesign();

  useEffect(() => {
    api
      .get<AccountOut>("/api/account")
      .then((res) => setTier(res.membership.tier))
      .catch(() => undefined);
  }, []);

  // Single source of truth for "go back", used by BOTH Telegram's native
  // hardware/header back button AND the in-app header's back arrow, so
  // the two can never disagree about where "back" leads.
  //
  // Checks overlayClose FIRST: if a picker modal (font/color/icon) is
  // currently open, it registered its own close function here, and
  // pressing back should just close that modal - not navigate the
  // screen underneath.
  const goBack = () => {
    if (overlayClose) {
      overlayClose();
      return;
    }
    setScreen((s) => {
      if (s === "finalize") return "editor";
      if (s === "editor") return editorOrigin === "recent" ? "recent" : editorOrigin === "textOnly" ? "createMenu" : "templates";
      if (s === "recent") return "createMenu";
      if (s === "templates") return "folders";
      if (s === "favorites") return "createMenu";
      if (s === "folders") return "createMenu";
      return s; // already on a root tab - nothing above it to go back to
    });
  };

  const handlePickTemplate = (tpl: TemplateOut) => {
    if (tpl.is_premium && tier === "free") {
      setBlockedTemplate(tpl);
      return;
    }
    setEditorOrigin("templates");
    startDesign(tpl);
    setScreen("editor");
  };

  // The bot's "/start <template_id>" deep link opens the Mini App with
  // ?template=<id> on the WebApp button's URL (Telegram passes this
  // straight through untouched for inline-keyboard WebApp buttons - its
  // own launch data goes in the URL *hash*, not the query string, so the
  // two never collide). Jump straight to that template's editor instead
  // of showing the account screen first.
  useEffect(() => {
    const templateId = new URLSearchParams(window.location.search).get("template");
    if (!templateId) {
      setResolvingDeepLink(false);
      return;
    }

    // Telegram's WebView can cache/reuse the exact URL a Mini App was
    // last opened with for this bot (e.g. via the persistent menu
    // button), even across separate sessions. Stripping the param from
    // the visible URL right away means a stale one can't linger into a
    // later, unrelated open and silently auto-load an old template
    // again - this only touches the address bar, not navigation, so it
    // doesn't affect anything already in flight below.
    window.history.replaceState(null, "", window.location.pathname);

    Promise.all([
      api.get<TemplateOut>(`/api/templates/${encodeURIComponent(templateId)}`),
      // Fetched fresh here rather than relying on the separate tier
      // state above - that one also loads on mount, independently,
      // with no guarantee it resolves before this deep-link effect
      // does. A free user opening a VIP template's direct link must
      // never slip through just because of that race.
      api.get<AccountOut>("/api/account").catch(() => null),
    ])
      .then(([tpl, account]) => {
        const currentTier = account?.membership.tier ?? tier;
        if (tpl.is_premium && currentTier === "free") {
          setBlockedTemplate(tpl);
          return;
        }
        handlePickTemplate(tpl);
      })
      .catch(() => {
        // Unknown/removed template - just land on the account screen instead of a dead end.
      })
      .finally(() => setResolvingDeepLink(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // goBack is recreated every render (it reads overlayClose from
  // context), but the effect below only re-runs when `screen` changes -
  // so the handler passed to showBackButton would otherwise close over
  // whatever overlayClose was AT THAT TIME, going stale the moment a
  // modal opens/closes afterward. Indirecting through a ref means the
  // registered handler is a stable reference (no need to re-subscribe
  // on every keystroke-triggered re-render) while still always calling
  // the CURRENT goBack.
  const goBackRef = useRef(goBack);
  goBackRef.current = goBack;

  useEffect(() => {
    if (ROOT_SCREENS.includes(screen)) {
      hideBackButton();
      return;
    }
    return showBackButton(() => goBackRef.current());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  const handleTextOnly = async () => {
    try {
      const blankTemplate = await api.get<TemplateOut>(`/api/templates/${BLANK_TEMPLATE_ID}`);
      setEditorOrigin("textOnly");
      startDesign(blankTemplate);
      setScreen("editor");
    } catch {
      // Silently ignore - user stays on the create menu and can retry.
    }
  };

  if (resolvingDeepLink) {
    return <div className="app-shell flex flex-col" />;
  }

  const activeTab = screenToTab(screen);
  const isRootScreen = ROOT_SCREENS.includes(screen);

  const handleTabChange = (tab: MainTab) => {
    if (tab === "home") setScreen("account");
    else if (tab === "packs") setScreen("packs");
    else if (tab === "plans") setScreen("plans");
    else if (tab === "guide") setScreen("guide");
    else setScreen("createMenu");
  };

  return (
    <div className="app-shell flex flex-col" data-screen={screen}>
      <div className="ambient-orb ambient-orb-1" />
      <div className="ambient-orb ambient-orb-2" />
      <div className="ambient-orb ambient-orb-3" />
      <div className="ambient-orb ambient-orb-4" />
      <div className="ambient-grain" />
      <div className="relative z-10 flex flex-col h-full">
        <RenderingToast />
        {blockedTemplate && <PremiumBlockedModal onClose={() => setBlockedTemplate(null)} />}
        {vipModal && <VipFeatureModal onClose={() => setVipModal(false)} />}

        {/* Only the logo/title header is truly persistent - the banner
            carousel is now positioned per-screen (AccountScreen shows
            it below the profile section, other screens near their own
            top), so it can't have one universal position across every
            screen at once. */}
        <AppHeader />

        <div className="flex-1 min-h-0">
          {screen === "account" && <AccountScreen onPickTemplate={handlePickTemplate} />}
          {screen === "createMenu" && (
            <CreateMenu
              onTemplates={() => setScreen("folders")}
              onTextOnly={handleTextOnly}
              onFavorites={() => setScreen("favorites")}
              onRecent={() => (tier === "vip" ? setScreen("recent") : setVipModal(true))}
            />
          )}
          {screen === "packs" && <PacksBrowser onBack={goBack} />}
          {screen === "plans" && <PlansScreen />}
          {screen === "guide" && <GuideScreen />}
          {screen === "folders" && (
            <FolderGrid
              onPick={(f) => {
                setSelectedFolder(f);
                setScreen("templates");
              }}
              onBack={goBack}
            />
          )}
          {screen === "templates" && (
            <TemplateGrid
              onPick={handlePickTemplate}
              onBack={goBack}
              folder={selectedFolder?.id}
              title={selectedFolder && selectedFolder.id !== "all" ? selectedFolder.name : undefined}
            />
          )}
          {screen === "favorites" && (
            <TemplateGrid favoritesOnly onPick={handlePickTemplate} onBack={goBack} />
          )}
          {screen === "recent" && (
            <RecentProjectsScreen
              onBack={goBack}
              onOpened={() => {
                setEditorOrigin("recent");
                setScreen("editor");
              }}
            />
          )}
          {screen === "editor" && (
            <EditorScreen
              onBack={goBack}
              onFinalized={(id) => {
                setStickerId(id);
                setScreen("finalize");
              }}
            />
          )}
          {screen === "finalize" && stickerId && (
            <PackPicker stickerId={stickerId} detectedEmoji={detectEmojiFromText(design?.texts[0]?.text)} onBack={goBack} onDone={() => setScreen("account")} />
          )}
        </div>

        {isRootScreen && <BottomNav active={activeTab} onChange={handleTabChange} tier={tier} />}
      </div>
    </div>
  );
}
